import type { ColumnState, FilterModel } from "ag-grid-enterprise";
import type { CalendarType } from "../components/cubeDataTable/columnDefinitions/columnFactory";
import type { ForecastTableControlValues } from "../components/tableControls/forecastTableControlValues";
import type { ForecastMetaDataType } from "../hooks/useFetchForecastMetaData";
import { presetMetrics } from "./builtInViews";
import type { ViewRows } from "./savedViewPayload";

/** Calendar family per calendar-view tab. */
export const CALENDAR_VIEW_TO_TYPE: Record<
  ForecastTableControlValues["calendarView"],
  CalendarType
> = {
  "Inv 454": "inventory_454",
  "Fin greg": "fiscal_gregorian",
  "Greg cal year": "gregorian"
};

/** Which column carries the year in a calendar family. */
export function yearColId(
  calendarType: CalendarType
): "year" | "fiscal_year_name" {
  return calendarType === "gregorian" ? "year" : "fiscal_year_name";
}

/** The year column of every calendar family. */
export const YEAR_COL_IDS: readonly string[] = [
  ...new Set(Object.values(CALENDAR_VIEW_TO_TYPE).map(yearColId))
];

/**
 * Column ids the row selection owns. A saved raw filter model contributes
 * every other column and never these — otherwise a stale hand-set filter
 * would fight the view that is being applied. ``producer_type`` is not
 * here: which producers show follows which cubes the planner loaded, so a
 * hand-set producer filter is a layout filter like any other. Nor is the
 * gregorian ``year`` column: the offsets never resolve onto it (see
 * ``resolveViewRows``), so a filter set there by hand is the planner's.
 */
const VIEW_OWNED_COL_IDS = ["metric_name", "fiscal_year_name"];

/**
 * Split a filter model into the entries on the columns the row selection
 * owns and the planner's own.
 */
export function partitionViewOwnedFilters(
  model: FilterModel | null | undefined
): { viewOwned: FilterModel; planner: FilterModel } {
  const viewOwned: FilterModel = {};
  const planner: FilterModel = {};
  for (const [colId, entry] of Object.entries(model ?? {})) {
    (VIEW_OWNED_COL_IDS.includes(colId) ? viewOwned : planner)[colId] = entry;
  }
  return { viewOwned, planner };
}

/** The grid state a row selection resolves to. */
export interface ResolvedViewState {
  /** Complete filter model to set; null clears every filter. */
  filterModel: FilterModel | null;
  /** Sort entries to apply; empty clears sorting. */
  sortModel: Pick<ColumnState, "colId" | "sort" | "sortIndex">[];
  /**
   * Counts the applies. A new identity under the same ``applyId`` is the
   * same view re-resolved (a calendar-family switch renames the year
   * column), which re-lands only the columns the view owns and keeps the
   * planner's hand-set filters; a new ``applyId`` — or none — replaces the
   * filter model wholesale.
   */
  applyId?: number;
}

/** The set-filter half of a multi filter, the shape the pinned columns use. */
export function setFilterModel(values: string[]) {
  return {
    filterType: "multi" as const,
    filterModels: [null, { filterType: "set" as const, values }]
  };
}

/**
 * The values a set filter (bare, or inside a multi filter) keeps, or null
 * when the model is absent or not a set filter.
 */
export function setFilterValues(model: unknown): string[] | null {
  if (!model || typeof model !== "object") return null;
  const candidate =
    "filterModels" in model && Array.isArray(model.filterModels)
      ? model.filterModels.find(
          (part) => part && typeof part === "object" && "values" in part
        )
      : model;
  if (!candidate || typeof candidate !== "object" || !("values" in candidate)) {
    return null;
  }
  const { values } = candidate as { values: unknown };
  return Array.isArray(values)
    ? values.filter((value): value is string => typeof value === "string")
    : null;
}

/**
 * Metric names a row selection keeps, or null for no restriction.
 *
 * The three sources union: the preset's list, every metric in a selected
 * category, and the metrics picked by hand. Union rather than intersection
 * because a category is a refinement *added* to a view ("OP plus the price
 * metrics"), which is how the old checkboxes behaved.
 */
export function selectedMetrics(
  rows: ViewRows,
  metricCategoryMapping: Record<string, string> | undefined
): string[] | null {
  const mapping = metricCategoryMapping ?? {};
  const allMetricNames = Object.keys(mapping);
  const preset = presetMetrics(rows.preset, allMetricNames);
  const categories = new Set(rows.categories);
  const fromCategories = categories.size
    ? allMetricNames.filter((metric) => categories.has(mapping[metric]))
    : [];
  // A preset that resolves to no metric (Per SQM before the mapping lands)
  // is no restriction either: a set filter with no values shows nothing.
  if (!preset?.length && !fromCategories.length && !rows.metrics.length) {
    return null;
  }
  return [...new Set([...(preset ?? []), ...fromCategories, ...rows.metrics])];
}

/** Fiscal-year names inside an offset window, per the live year mapping. */
function selectedYears(
  yearOffsets: [number, number],
  yearMapping: Record<string, number> | undefined
): string[] {
  if (!yearMapping) return [];
  const [min, max] = yearOffsets;
  return Object.entries(yearMapping)
    .filter(([, offset]) => offset >= min && offset <= max)
    .map(([year]) => year);
}

/**
 * Resolve a row selection into the filter + sort state to put on the grid.
 *
 * Everything is resolved here, against live metadata and the current
 * calendar family, so a stored view carries intent (a preset key, an offset
 * window, a sort key) rather than colIds and metric lists that go stale.
 */
export function resolveViewRows(
  rows: ViewRows | undefined,
  options: {
    /** Metric mapping + year mapping; undefined until metadata lands. */
    metaData: ForecastMetaDataType | undefined;
    /** Calendar tab in force, which decides the year column's id. */
    calendarView: ForecastTableControlValues["calendarView"];
    /** Column filters the user set by hand, replayed alongside. */
    extraFilterModel?: FilterModel | null;
  }
): ResolvedViewState {
  const { metaData, calendarView, extraFilterModel } = options;
  const yearColumn = yearColId(CALENDAR_VIEW_TO_TYPE[calendarView]);
  const filterModel = partitionViewOwnedFilters(extraFilterModel).planner;

  if (!rows) {
    return {
      filterModel: Object.keys(filterModel).length ? filterModel : null,
      sortModel: []
    };
  }

  // A preset and a hand-picked metric list resolve without metadata; only
  // categories and the Per SQM rule need the mapping.
  const metrics = selectedMetrics(rows, metaData?.metricCategoryMapping);
  if (metrics) filterModel.metric_name = setFilterModel(metrics);
  // The offsets index ``yearMapping``, which is fiscal-only. The gregorian
  // year column holds calendar years, and one fiscal year spans two of them,
  // so there is nothing honest to filter on — leave every year visible. The
  // same until the mapping lands: a set filter with no values shows nothing.
  const years = rows.yearOffsets
    ? selectedYears(rows.yearOffsets, metaData?.yearMapping)
    : [];
  if (years.length && yearColumn !== "year") {
    filterModel[yearColumn] = setFilterModel(years);
  }

  return {
    filterModel: Object.keys(filterModel).length ? filterModel : null,
    sortModel: resolveSortModel(rows.sortBy, yearColumn)
  };
}

/** Sort entries for a sort key. "Trans" follows the year column's family. */
function resolveSortModel(
  sortBy: ViewRows["sortBy"],
  yearColumn: string
): Pick<ColumnState, "colId" | "sort" | "sortIndex">[] {
  switch (sortBy) {
    case "Trans":
      return [{ colId: yearColumn, sort: "desc", sortIndex: 0 }];
    case "YoY":
      return [{ colId: "metric_name", sort: "asc", sortIndex: 0 }];
    case "None":
    default:
      return [];
  }
}
